/**
 * LensPanel.jsx — ATTIO OBJECT INTELLIGENCE PANEL
 *
 * MICRO PARITY V3:
 *   Entry is simple. Interpretation happens in context.
 *   Orb = entry point. Sheet = interpretation layer.
 *
 *   Mode tabs [ Cause ] [ Rule ] [ Memory ] live INSIDE the sheet top bar.
 *   No radial selector. No orb-level mode indicators.
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ChevronRight, Zap, GitBranch, Shield, Clock, Brain, Scale, FileText } from 'lucide-react';
import { useGranulloLensV2, INSPECTION_MODES } from './GranulloLensV2Provider';
import { LENS_TYPES } from './LensAnnotations';

const MODE_COLORS = {
  CAUSE: 'var(--g-editorial-red, #CD2200)',
  RULE:  'var(--g-action-blue, #2763E7)',
  MEMORY:'#7C3AED',
};

const MODE_KEYS = ['CAUSE', 'RULE', 'MEMORY'];

const TYPE_LABELS = {
  [LENS_TYPES.PROGRAM]: 'Program',
  [LENS_TYPES.RULE_PACK]: 'Rule Pack',
  [LENS_TYPES.PRESSURE_CARD]: 'Pressure Card',
  [LENS_TYPES.MISUSE_FUTURE]: 'Misuse Future',
  [LENS_TYPES.STRUCTURAL_FIX]: 'Structural Fix',
  [LENS_TYPES.PAYMENT_TWIN]: 'Payment Twin',
  [LENS_TYPES.GRANULLO_PASS]: 'Granullo Pass',
  [LENS_TYPES.CONSEQUENCE_MEMORY]: 'Consequence Memory',
  [LENS_TYPES.BENEFICIARY_OUTCOME]: 'Beneficiary Outcome',
  [LENS_TYPES.SCENARIO_CARD]: 'Scenario',
  [LENS_TYPES.TIMELINE_EVENT]: 'Timeline Event',
  [LENS_TYPES.TRUST_STATE]: 'Trust State',
  [LENS_TYPES.NAV_ITEM]: 'Navigation',
  [LENS_TYPES.CTA]: 'Action',
  [LENS_TYPES.METRIC]: 'Metric',
  [LENS_TYPES.SECTION]: 'Section',
  [LENS_TYPES.TOPOLOGY_NODE]: 'Topology Node',
  [LENS_TYPES.RELATIONSHIP_LINE]: 'Relationship',
  [LENS_TYPES.PLAY_ACT]: 'Play Act',
  [LENS_TYPES.PLAY_ARTIFACT]: 'Artifact',
  [LENS_TYPES.PLAY_EVIDENCE]: 'Evidence Object',
  [LENS_TYPES.PLAY_POLICY]: 'Policy Object',
  [LENS_TYPES.PLAY_VISUALIZATION]: 'Visualization',
  [LENS_TYPES.PLAY_HERO]: 'Hero Section',
  [LENS_TYPES.PLAY_CTA]: 'Conviction CTA',
  'theorem': 'Theorem',
  'chart': 'Chart Argument',
  'evidence': 'Evidence',
  'hero': 'Hero Statement',
};

/* ═══ MODE TABS — Attio segmented control ═══ */
function ModeTabs({ inspectionMode, setInspectionMode }) {
  return (
    <div
      className="flex items-center gap-0.5 p-0.5 rounded-lg"
      style={{ background: 'var(--surface-recessed, #F3F4F1)' }}
      data-testid="lens-mode-tabs"
    >
      {MODE_KEYS.map((mode) => {
        const isActive = (inspectionMode || 'CAUSE') === mode;
        const color = MODE_COLORS[mode];
        return (
          <button
            key={mode}
            onClick={() => setInspectionMode(mode)}
            className="relative px-3 py-1.5 text-[10px] font-mono tracking-[0.04em] rounded-md transition-all cursor-pointer"
            style={{
              background: isActive ? 'var(--surface-bg, #FFFFFF)' : 'transparent',
              color: isActive ? color : 'var(--text-muted, #94A3B8)',
              boxShadow: isActive ? '0 1px 3px rgba(15,23,42,0.06)' : 'none',
              fontWeight: isActive ? 600 : 500,
            }}
            data-testid={`lens-mode-${mode.toLowerCase()}`}
          >
            {mode}
          </button>
        );
      })}
    </div>
  );
}

/* ═══ SECTION BLOCK ═══ */
function Section({ label, icon: Icon, color, children }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-1.5 mb-2.5">
        {Icon && <Icon size={11} style={{ color: color || 'var(--text-muted, #94A3B8)' }} />}
        <p className="text-[10px] font-mono tracking-[0.08em] uppercase" style={{ color: 'var(--text-muted, #94A3B8)' }}>
          {label}
        </p>
      </div>
      {children}
    </div>
  );
}

/* ═══ CAUSE MODE CONTENT ═══ */
function CauseModeContent({ response }) {
  return (
    <>
      <Section label="Origin event" icon={GitBranch} color={MODE_COLORS.CAUSE}>
        <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-secondary, #475569)' }}>
          {response.causeOrigin}
        </p>
      </Section>

      {response.causalDependency?.length > 0 && (
        <Section label="Causal dependency" icon={ArrowRight}>
          <div className="flex flex-wrap gap-1.5">
            {response.causalDependency.map((dep, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px]"
                style={{ background: `${MODE_COLORS.CAUSE}08`, border: `1px solid ${MODE_COLORS.CAUSE}15`, color: MODE_COLORS.CAUSE }}>
                {TYPE_LABELS[dep] || dep}
              </span>
            ))}
          </div>
        </Section>
      )}

      <Section label="Risk introduction" icon={Shield} color={MODE_COLORS.CAUSE}>
        <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-secondary, #475569)' }}>
          {response.riskIntroduction}
        </p>
      </Section>

      <Section label="Structural change" icon={Zap}>
        <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-secondary, #475569)' }}>
          {response.structuralChange}
        </p>
      </Section>
    </>
  );
}

/* ═══ RULE MODE CONTENT ═══ */
function RuleModeContent({ response }) {
  return (
    <>
      <Section label="Rule logic" icon={Scale} color={MODE_COLORS.RULE}>
        <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-secondary, #475569)' }}>
          {response.ruleLogic}
        </p>
      </Section>

      <Section label="Pass conditions" icon={Shield}>
        <div className="space-y-1.5">
          {response.passConditions?.map((cond, i) => (
            <div key={i} className="flex items-start gap-2 text-[11px] px-3 py-2 rounded-md"
              style={{ background: 'rgba(39, 99, 231, 0.04)', color: 'var(--text-secondary, #475569)' }}>
              <span style={{ color: '#22C55E', fontSize: 10, marginTop: 2 }}>PASS</span>
              {cond}
            </div>
          ))}
        </div>
      </Section>

      <Section label="Fail conditions" icon={Shield}>
        <div className="space-y-1.5">
          {response.failConditions?.map((cond, i) => (
            <div key={i} className="flex items-start gap-2 text-[11px] px-3 py-2 rounded-md"
              style={{ background: `${MODE_COLORS.CAUSE}04`, color: 'var(--text-secondary, #475569)' }}>
              <span style={{ color: MODE_COLORS.CAUSE, fontSize: 10, marginTop: 2 }}>FAIL</span>
              {cond}
            </div>
          ))}
        </div>
      </Section>

      <Section label="Authority source" icon={FileText}>
        <p className="text-[11px] font-mono" style={{ color: 'var(--text-muted, #94A3B8)' }}>
          {response.authoritySource}
        </p>
      </Section>

      <Section label="Human-readable">
        <div className="px-3 py-3 rounded-lg" style={{ background: 'var(--surface-recessed, #F7F8F6)', border: '1px solid var(--border-soft, rgba(15,23,42,0.06))' }}>
          <p className="text-[13px] leading-relaxed italic" style={{ color: 'var(--text-primary, #111827)' }}>
            {response.humanSentence}
          </p>
        </div>
      </Section>
    </>
  );
}

/* ═══ MEMORY MODE CONTENT ═══ */
function MemoryModeContent({ response }) {
  return (
    <>
      <Section label="What was learned" icon={Brain} color={MODE_COLORS.MEMORY}>
        <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-secondary, #475569)' }}>
          {response.whatWasLearned}
        </p>
      </Section>

      <Section label="Future decision impact" icon={ArrowRight}>
        <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-secondary, #475569)' }}>
          {response.futureDecisionImpact}
        </p>
      </Section>

      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="text-center py-3 px-2 rounded-lg" style={{ background: `${MODE_COLORS.MEMORY}06`, border: `1px solid ${MODE_COLORS.MEMORY}12` }}>
          <p className="text-[18px] font-semibold" style={{ color: MODE_COLORS.MEMORY, fontVariantNumeric: 'tabular-nums' }}>
            {response.replayCount}
          </p>
          <p className="text-[9px] font-mono uppercase tracking-wider" style={{ color: 'var(--text-muted, #94A3B8)' }}>
            Replay count
          </p>
        </div>
        <div className="text-center py-3 px-2 rounded-lg" style={{ background: `${MODE_COLORS.MEMORY}06`, border: `1px solid ${MODE_COLORS.MEMORY}12` }}>
          <p className="text-[18px] font-semibold" style={{ color: MODE_COLORS.MEMORY, fontVariantNumeric: 'tabular-nums' }}>
            {response.preventedDownstreamFutures}
          </p>
          <p className="text-[9px] font-mono uppercase tracking-wider" style={{ color: 'var(--text-muted, #94A3B8)' }}>
            Futures prevented
          </p>
        </div>
      </div>

      <Section label="Memory mutation impact" icon={Clock}>
        <div className="px-3 py-2.5 rounded-lg" style={{ background: `${MODE_COLORS.MEMORY}05`, border: `1px solid ${MODE_COLORS.MEMORY}10` }}>
          <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-secondary, #475569)' }}>
            {response.memoryMutationImpact}
          </p>
        </div>
      </Section>
    </>
  );
}

/* ═══ DESKTOP PANEL ═══ */
function DesktopPanel({ annotation, response, inspectionMode, setInspectionMode, onClose }) {
  const modeColor = MODE_COLORS[inspectionMode] || MODE_COLORS.CAUSE;

  return (
    <motion.aside
      data-lens-panel="true"
      data-testid="lens-v2-panel"
      initial={{ opacity: 0, x: 20, scale: 0.98 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 20, scale: 0.98 }}
      transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
      className="fixed top-4 right-4 bottom-4 z-[61] w-[360px] overflow-y-auto rounded-xl"
      style={{
        background: 'var(--surface-bg, #FFFFFF)',
        border: '1px solid var(--border-soft, rgba(15,23,42,0.08))',
        boxShadow: '0 4px 16px rgba(15,23,42,0.06), 0 16px 48px rgba(15,23,42,0.08)',
      }}
    >
      <div className="p-6">
        {/* ═══ HEADER ═══ */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <span className="text-[9px] font-mono tracking-[0.06em] uppercase px-1.5 py-0.5 rounded mb-2 inline-block"
              style={{ background: 'var(--surface-recessed, #F3F4F1)', color: 'var(--text-muted, #94A3B8)' }}>
              {TYPE_LABELS[annotation.type] || annotation.type}
            </span>
            <h3 className="text-[15px] font-semibold leading-snug tracking-tight" style={{ color: 'var(--text-primary, #111827)' }}>
              {annotation.title}
            </h3>
          </div>
          <button onClick={onClose} data-testid="lens-v2-panel-close"
            className="p-1.5 rounded-md transition-colors cursor-pointer ml-3 flex-shrink-0"
            style={{ color: 'var(--text-muted, #94A3B8)' }}>
            <X size={16} />
          </button>
        </div>

        {/* ═══ MODE TABS — inside sheet ═══ */}
        <div className="mb-5">
          <ModeTabs inspectionMode={inspectionMode} setInspectionMode={setInspectionMode} />
        </div>

        {/* ═══ HUMAN-READABLE EXPLANATION ═══ */}
        {annotation.meaning && (
          <div className="mb-5 px-3 py-3 rounded-lg" style={{ background: 'var(--surface-recessed, #F7F8F6)', borderLeft: `3px solid ${modeColor}` }}>
            <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-secondary, #475569)' }}>
              {annotation.meaning}
            </p>
          </div>
        )}

        {/* ═══ MODE-SPECIFIC CONTENT ═══ */}
        {inspectionMode === 'CAUSE' && <CauseModeContent response={response} />}
        {inspectionMode === 'RULE' && <RuleModeContent response={response} />}
        {inspectionMode === 'MEMORY' && <MemoryModeContent response={response} />}

        {/* ═══ ACTIONS ═══ */}
        {annotation.actions?.length > 0 && (
          <Section label="Available actions" icon={Zap}>
            <div className="space-y-1.5">
              {annotation.actions.map((action, i) => (
                <div key={i} className="flex items-center gap-2.5 text-[11px] px-3 py-2 rounded-md cursor-pointer transition-colors"
                  style={{ background: 'var(--surface-recessed, #F7F8F6)', color: 'var(--text-secondary, #475569)' }}>
                  <ChevronRight size={10} style={{ color: modeColor }} />
                  {action}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* ═══ RELATIONSHIP CHAIN ═══ */}
        {response?.whatItRelatesTo?.length > 0 && (
          <Section label="Related objects" icon={GitBranch}>
            <div className="flex flex-wrap gap-1.5">
              {response.whatItRelatesTo.map((rel, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px]"
                  style={{ background: 'var(--surface-recessed, #F7F8F6)', color: 'var(--text-muted, #64748B)' }}>
                  {rel.direction === 'upstream' && <ArrowRight size={8} className="rotate-180" />}
                  {TYPE_LABELS[rel.type] || rel.type}
                  {rel.direction === 'downstream' && <ArrowRight size={8} />}
                </span>
              ))}
            </div>
          </Section>
        )}

        {/* ═══ SUGGESTED NEXT ═══ */}
        {response?.whatHappensNext?.length > 0 && (
          <Section label="Next in chain">
            {response.whatHappensNext.map((next, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px] mb-1" style={{ color: 'var(--text-muted, #64748B)' }}>
                <ChevronRight size={10} /> {TYPE_LABELS[next] || next}
              </div>
            ))}
          </Section>
        )}

        {/* ═══ FOOTER ═══ */}
        <div className="mt-6 pt-4 flex items-center justify-between" style={{ borderTop: '1px solid var(--border-soft, rgba(15,23,42,0.06))' }}>
          <span className="text-[9px] font-mono" style={{ color: 'var(--text-muted, #94A3B8)' }}>{annotation.id}</span>
          <span className="text-[9px] font-mono" style={{ color: modeColor }}>LENS</span>
        </div>
      </div>
    </motion.aside>
  );
}

/* ═══ MOBILE SHEET ═══ */
function MobileSheet({ annotation, response, inspectionMode, setInspectionMode, onClose }) {
  const modeColor = MODE_COLORS[inspectionMode] || MODE_COLORS.CAUSE;

  return (
    <motion.div
      data-lens-panel="true"
      data-testid="lens-v2-mobile-sheet"
      initial={{ opacity: 0, y: '100%' }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: '100%' }}
      transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
      className="fixed left-0 right-0 bottom-0 z-[61] max-h-[70vh] overflow-y-auto rounded-t-2xl"
      style={{
        background: 'var(--surface-bg, #FFFFFF)',
        border: '1px solid var(--border-soft, rgba(15,23,42,0.08))',
        borderBottom: 'none',
        boxShadow: '0 -4px 24px rgba(15,23,42,0.08)',
      }}
    >
      <div className="flex justify-center pt-3 pb-1">
        <div className="w-8 h-1 rounded-full" style={{ background: 'var(--border-soft, rgba(15,23,42,0.12))' }} />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[13px] font-semibold flex-1" style={{ color: 'var(--text-primary, #111827)' }}>
            {annotation.title}
          </span>
          <button onClick={onClose} className="p-1" style={{ color: 'var(--text-muted, #94A3B8)' }}>
            <X size={16} />
          </button>
        </div>

        {/* Mode tabs — inside mobile sheet */}
        <div className="mb-4">
          <ModeTabs inspectionMode={inspectionMode} setInspectionMode={setInspectionMode} />
        </div>

        {annotation.meaning && (
          <p className="text-[12px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary, #475569)' }}>
            {annotation.meaning}
          </p>
        )}
        {inspectionMode === 'CAUSE' && <CauseModeContent response={response} />}
        {inspectionMode === 'RULE' && <RuleModeContent response={response} />}
        {inspectionMode === 'MEMORY' && <MemoryModeContent response={response} />}
      </div>
    </motion.div>
  );
}

/* ═══ MAIN EXPORT ═══ */
export function LensPanel() {
  const { focusedAnnotation, lensResponse, inspectionMode, setInspectionMode, isMobile, closeLens } = useGranulloLensV2();

  return (
    <AnimatePresence>
      {focusedAnnotation && lensResponse && (
        isMobile ? (
          <MobileSheet
            key="lens-mobile"
            annotation={focusedAnnotation}
            response={lensResponse}
            inspectionMode={inspectionMode || 'CAUSE'}
            setInspectionMode={setInspectionMode}
            onClose={closeLens}
          />
        ) : (
          <DesktopPanel
            key="lens-desktop"
            annotation={focusedAnnotation}
            response={lensResponse}
            inspectionMode={inspectionMode || 'CAUSE'}
            setInspectionMode={setInspectionMode}
            onClose={closeLens}
          />
        )
      )}
    </AnimatePresence>
  );
}

export default LensPanel;
